import { useMetrics } from '@cognite/metrics';
import { METRICS_NAMESPACES } from '@infield/features/metrics';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { KeyboardConfig, KeyboardType } from '../device-context';

export const KEYBOARD_MODE_LOCAL_STORAGE_VARIABLE = 'USER_KEYBOARD_MODE';

const getKeyboardConfig = (mode: KeyboardType): KeyboardConfig => {
  if (mode === 'IOS' || mode === 'WINDOWS') {
    return {
      type: 'number',
      step: 1e-64,
    };
  }
  if (mode === 'ANDROID') {
    return {
      type: 'text',
      inputMode: 'numeric',
    };
  }
  return {
    type: 'text',
  };
};

const useKeyboardMode = (isEnabled: Boolean) => {
  const metrics = useMetrics(METRICS_NAMESPACES.keyboardSettings);
  const [keyboardMode, setKeyboardMode] = useState<KeyboardType>('DEFAULT');
  const setKeyboardModeManually = useCallback(
    (mode: KeyboardType) => {
      if (!isEnabled) return;
      setKeyboardMode(mode);
      metrics.track('keyboard_mode_changed_manually', { mode });
      localStorage.setItem(KEYBOARD_MODE_LOCAL_STORAGE_VARIABLE, mode);
    },
    [metrics, isEnabled]
  );
  const detectKeyboardMode = (): KeyboardType => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) return 'IOS';
    if (/Android/i.test(ua)) return 'ANDROID';
    if (/Windows/i.test(ua)) return 'WINDOWS';
    return 'DEFAULT';
  };

  useEffect(() => {
    if (!isEnabled) return;
    const storedKeyboardMode = localStorage.getItem(
      KEYBOARD_MODE_LOCAL_STORAGE_VARIABLE
    ) as KeyboardType;
    if (storedKeyboardMode) {
      metrics.track('keyboard_mode_set_from_localStorage', {
        storedKeyboardMode,
      });
      setKeyboardMode(storedKeyboardMode);
    } else {
      const detectedMode = detectKeyboardMode();
      metrics.track('keyboard_mode_set_from_detection', {
        detectedMode,
      });
      setKeyboardMode(detectedMode);
    }
  }, [metrics, isEnabled]);

  const keyboardConfig = useMemo(
    () => getKeyboardConfig(keyboardMode),
    [keyboardMode]
  );
  return {
    keyboardMode,
    setKeyboardModeManually,
    keyboardConfig,
  };
};
export default useKeyboardMode;
